import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import CustomTable from '../CustomTable'
import CustomDomainSelectorModal from '../CustomDomainSelectorModal/CustomDomainSelectorModal'
import IntegrationsModal from '../OptionsModal/IntegrationsModal'
import {
  copyToClipboard,
  getDirectFileLink,
  hasFeature,
  sortDuplicateSites,
  sortLoadingSites,
  parseFilename,
  onUpgradePlan,
  isWasmerFile
} from '../../utils/general'
import { setLoadingSitesStore, isPresignedUploadInProgress, showMessageModal } from 'Manage/actions'
import { F_EDIT_MODE } from '../../constants/plans/constants'
import { ACTION_UPDATE, FILE_TYPE_PDF } from '../CreateUpdateSiteModal/interface'
import { ACCOUNT_STATUS, API_ROOT } from '../../constants'
import { getSafeFileName } from 'helpers/file-type'
import { getSites, exportCollectedEmails } from 'services/site'
import { PLANS } from 'constants/plans'
import UploadDropdown from '../UploadDropDown'
import useCheckIsMobileScreen from '../../hooks/checkMobileScreen'
import { isDevSite, isCustomDomain } from '../../utils/general'
import { cancelPresignedUpload } from 'services/presigned-uploads/handlePresignedUploads'
import { getSitePageviews } from '../../services/manage'
import { clearDomainCache } from '../../services/custom-domain'
import { clearCacheSuccessMessage, clearCacheErrorMessage } from '../CustomDomainTable'

import './LiveSitesTable.css'
import BorderButton from '../BorderButton'
import { Badge } from 'react-bootstrap'
import CreateWithAIModal from '../CreateWithAIModal/CreateWithAIModal'
import { isGameShowcaseEligibleSite } from '../../services/games'

const getDomain = (siteRecord) => siteRecord.subdomain || siteRecord.link

function getDirectFileLinkForSite(s) {
  if ((s?.isPresignedUpload || s?.linkStatus !== 'processing') && s?.linkStatus !== 'error')
    if (s.type === FILE_TYPE_PDF) {
      return `https://${getDomain(s)}/${getSafeFileName(s.filename)}`
    } else {
      // legacy links
      return getDirectFileLink(`https://${getDomain(s)}`, s.filename, 'pdf', s.lastUpdated || s.created)
    }
}

const getLinkStatusText = (s) => {
  switch (s) {
    case 'processing':
      return 'Processing'
    case 'error':
      return 'Failed'
    case 'preview':
      return 'Preview'
    case 'archived':
      return 'Archived'
    default:
      return 'Public'
  }
}

const LiveSitesTable = ({
  sites,
  onSiteAction,
  onViewVersions,
  userProfile,
  onArchive,
  onAssignToSpace,
  onDelete,
  onDownload,
  onDownloadAll,
  onAnalytics,
  onQRCode,
  onEditPDF,
  onEmbed,
  onPreview,
  onShare,
  onUpdate,
  onOpenTextEditor,
  onAddCustomDomain,
  badge,
  onShowCodeEditor,
  trialAccount,
  connectGithub,
  onAnalyseSite,
  viewRepos,
  onOpenUrl,
  onOpenGithubModal,
  onOpenTransferRequestLinkModal,
  onOpenDropBox,
  customDomains = [],
  currentSpaceName,
  activeSpace,
  onEditSpace,
  onDeleteSpace,
  hideSpacesProjects,
  onToggleHideSpacesProjects,
  onSubmitGame,
  gameShowcaseStatuses = {}
}) => {
  const { isMobile, isTablet } = useCheckIsMobileScreen()
  const dispatch = useDispatch()
  const { spaces } = useSelector((state) => state.manage)
  const hasSpaces = spaces && spaces.length > 0
  const [allSites, setAllSites] = useState([])
  const [onlineSites, setOnlineSites] = useState([])
  const [loadingSites, setLoadingSites] = useState([])
  const [webhook, setWebhook] = useState(false)
  const [webhookId, setWebhookId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const lastLoadingSitesStrRef = useRef('')
  const [viewsData, setViewsData] = useState({})
  const [viewsTimestamps, setViewsTimestamps] = useState({})
  const [viewsError, setViewsError] = useState(false)
  const viewsFetchingRef = useRef(false)
  const [showCreateWithAIModal, setShowCreateWithAIModal] = useState(false)
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false)

  const isFreeUser = !userProfile?.productId
  const shouldShowViewsColumn = isFreeUser && !isMobile

  const getHoursRemainingTooltip = (timestamp) => {
    if (!timestamp) return 'Click to see real time data'

    const secondsAgo = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
    const minutesAgo = Math.floor(secondsAgo / 60)
    const hoursAgo = Math.floor(minutesAgo / 60)

    let timeText
    if (hoursAgo > 0) {
      timeText = `Updated ${hoursAgo} hr${hoursAgo !== 1 ? 's' : ''} ago`
    } else if (minutesAgo > 0) {
      timeText = `Updated ${minutesAgo}m ago`
    } else {
      timeText = `Updated ${secondsAgo}s ago`
    }

    return `${timeText}. Click to see real time data`
  }

  const fetchViewsForSite = async (domain) => {
    const cacheKey = `viewsData_${domain}`
    const cached = localStorage.getItem(cacheKey)

    if (cached) {
      try {
        const { views, timestamp } = JSON.parse(cached)
        const sixHoursInMs = 6 * 60 * 60 * 1000
        const now = Date.now()

        if (timestamp && now - timestamp < sixHoursInMs) {
          return { views, timestamp }
        }
      } catch {
        localStorage.removeItem(cacheKey)
      }
    }

    try {
      const data = await getSitePageviews(domain)
      const views = data?.data[0]?.pageviews
      const timestamp = Date.now()

      localStorage.setItem(cacheKey, JSON.stringify({ views, timestamp }))
      return { views, timestamp }
    } catch (error) {
      console.error('fetchViewsForSite', error)
      throw error
    }
  }

  const validatedCustomDomains = customDomains.filter(
    (domain) => domain.status && ['CONNECTED', 'VALIDATED'].includes(domain.status.toUpperCase())
  )

  const [customDomainModalData, setCustomDomainModalData] = useState(null)

  const handlePublishCustomDomain = (site) => {
    const siteDomain = getDomain(site)
    const isServerFile = siteDomain?.includes('tiiny.io') || isWasmerFile(site)

    if (isServerFile) {
      localStorage.setItem('isServerFileForCustomDomain', 'true')
    } else {
      localStorage.removeItem('isServerFileForCustomDomain')
    }

    if (site?.wasmerDnsRecord) {
      onAddCustomDomain(site)
      return
    }

    // For PHP files, skip the domain selector modal and go directly to add custom domain
    if (isServerFile) {
      onAddCustomDomain(site)
      return
    }

    if (validatedCustomDomains.length > 0) {
      setCustomDomainModalData({
        site,
        validatedDomains: validatedCustomDomains
      })
    } else {
      onAddCustomDomain(site)
    }
  }

  const isCancelPresignedUpload = localStorage.getItem('cancelPresignedUpload')

  /** Webhook for processing sites */
  useEffect(() => {
    return () => {
      if (webhook === true) deleteWebhook()
    }
  }, [])

  useEffect(() => {
    setAllSites(sites)
  }, [sites])

  useEffect(() => {
    if (!shouldShowViewsColumn || viewsFetchingRef.current || allSites.length === 0 || viewsError) return
    viewsFetchingRef.current = true
    if (allSites.length > 0) {
      fetchViewsForSite(allSites[0].subdomain)
        .then(({ views, timestamp }) => {
          setViewsData({ [allSites[0].subdomain]: views })
          setViewsTimestamps({ [allSites[0].subdomain]: timestamp })
          setViewsError(false)
        })
        .catch(() => {
          setViewsError(true)
        })
        .finally(() => {
          viewsFetchingRef.current = false
        })
    } else {
      viewsFetchingRef.current = false
    }
  }, [shouldShowViewsColumn, allSites.length, viewsError])

  useEffect(() => {
    setLoadingSites(sortLoadingSites(allSites))
  }, [allSites])

  useEffect(() => {
    // Only dispatch to the store when loadingSites actually changes to avoid
    // triggering subscribers unnecessarily (prevents render loops)
    const curr = JSON.stringify(loadingSites)
    if (lastLoadingSitesStrRef.current !== curr) {
      dispatch(setLoadingSitesStore(loadingSites))
      lastLoadingSitesStrRef.current = curr
    }
  }, [loadingSites, dispatch])

  useEffect(() => {
    if (loadingSites?.length > 0) {
      setWebhook(true)
    } else if (loadingSites?.length === 0) {
      setWebhook(false)
    }
  }, [loadingSites])

  useEffect(() => {
    if (onlineSites?.length > 0) {
      setAllSites(sortDuplicateSites(allSites, onlineSites))
      setOnlineSites([])
    }
  }, [onlineSites])

  useEffect(() => {
    if (webhook === true) {
      createWebhook(loadingSites)
    } else if (webhook === false) {
      deleteWebhook()
    }
  }, [webhook])

  const getSiteWebhook = async () => {
    let data
    try {
      const response = await getSites({
        sites: loadingSites,
        /** Add timestamp to prevent caching */
        _t: new Date().getTime()
      })
      data = response.data
    } catch (error) {
      console.error('getSiteWebhook', error)
    }

    if (data?.sites?.some((s) => s.isPresignedUpload && s.linkStatus === 'processing')) {
      dispatch(isPresignedUploadInProgress(true))
    }

    if (data?.sites) {
      let myOnlineSites = []

      data.sites.map((site) => {
        if (site.linkStatus === 'online' || site.linkStatus === 'error') {
          myOnlineSites.push(site)
        }
      })

      setLoadingSites(sortLoadingSites(data.sites))

      if (myOnlineSites.length > 0) {
        setOnlineSites(myOnlineSites)
      }
    }
  }

  const createWebhook = () => {
    const intervalId = setInterval(getSiteWebhook, 10000)

    setWebhookId(intervalId)
  }

  const deleteWebhook = () => {
    clearInterval(webhookId)
    setWebhookId('')
    dispatch(isPresignedUploadInProgress(false))
  }

  const onEditHTML = (domain, isTrial) => {
    if (isDevSite()) {
      window.open(
        `https://www.tiiny.co.uk/editor/?domain=${domain}&file=/index.html${isTrial ? '&trial=true' : ''}`,
        '_blank'
      )
    } else {
      window.open(
        `https://tiiny.host/editor/?domain=${domain}&file=/index.html${isTrial ? '&trial=true' : ''}`,
        '_blank'
      )
    }
  }

  const handleEditPdfWithPSWD = (password, fileLink) => {
    if (password) {
      const token = btoa(password)
      const proxyUrl = `${API_ROOT}/v3/editor/set-cookie-and-proxy?url=${encodeURIComponent(fileLink)}&token=${encodeURIComponent(
        token
      )}`
      onEditPDF(proxyUrl)
    } else {
      onEditPDF(fileLink)
    }
  }

  const handleEditPDF = (site) => {
    const fileLink = getDirectFileLinkForSite(site)
    handleEditPdfWithPSWD(site?.password, fileLink)
  }

  const handleEditHTML = (site) => {
    const domain = getDomain(site)
    onEditHTML(domain, !hasFeature(userProfile.productId, F_EDIT_MODE))
  }

  const onUpgrade = (planId) => {
    onUpgradePlan({ planId, userProfile }, dispatch)
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
  }

  const handleExportEmails = async (site) => {
    try {
      const response = await exportCollectedEmails(site?.subdomain)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `collected-emails-${site.subdomain}.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export emails:', error)
    }
  }

  const filteredData = useMemo(() => {
    return allSites.filter((site) => getDomain(site).toLowerCase().includes(searchTerm.toLowerCase()))
  }, [allSites, searchTerm])

  const headers = [
    { key: 'status', text: 'Status', icon: '', cellType: 'status' },
    { key: 'domain', text: 'Project', icon: '', cellType: 'domain' },
    { key: 'lastModified', text: 'Modified', icon: '', cellType: 'lastModified' },
    ...(shouldShowViewsColumn ? [{ key: 'views', text: 'Views', icon: '', cellType: 'views' }] : []),
    { key: 'actions', text: 'Actions', icon: '', cellType: 'actions' }
  ]

  const canNotUpdate = (site) => site?.linkStatus === 'processing'

  const onClearCache = (site) => {
    clearDomainCache({ domain: getDomain(site) })
      .then(({ data }) => {
        dispatch(showMessageModal(data?.success ? clearCacheSuccessMessage : clearCacheErrorMessage))
      })
      .catch(() => {
        dispatch(showMessageModal(clearCacheErrorMessage))
      })
  }

  const data = filteredData.map((s) => ({
    status: {
      status: s?.linkStatus,
      text: getLinkStatusText(s?.linkStatus),
      passwordProtected: s.passwordProtected,
      emailProtected: s.enableEmailWhitelist,
      ssoProtected: s.enableSSO
    },
    domain: {
      label: getDomain(s),
      href: `https://${getDomain(s)}`,
      site: s,
      handlePreviewClick: onPreview,
      disabled: (s?.linkStatus === 'processing' && !s?.isPresignedUpload) || s?.linkStatus === 'error',
      trialAccount,
      isTransferRequestLink: s?.isTransferRequestLink || false,
      showSubmitGame: isGameShowcaseEligibleSite(s),
      onSubmitGame: () => onSubmitGame?.(s),
      // Keyed by s.subdomain to match getGameShowcaseStatuses/getUnsubmittedEligibleGameSites in
      // services/games.js, which key off the raw site.subdomain field, not getDomain(s).
      gameShowcaseStatus: gameShowcaseStatuses[s.subdomain] || null,
      status: s?.linkStatus,
      statusText: getLinkStatusText(s?.linkStatus),
      passwordProtected: s.passwordProtected,
      emailProtected: s.enableEmailWhitelist
    },
    lastModified: isMobile ? (
      <button
        type="button"
        className="live-sites-mobile-update"
        onClick={() => onUpdate(s)}
        disabled={canNotUpdate(s)}
        data-testid="LiveSitesTable-button-mobile-update"
      >
        Update
      </button>
    ) : s?.lastUpdated || s?.created ? (
      moment(s.lastUpdated || s.created).format('DD MMM YY')
    ) : (
      '-'
    ),
    ...(shouldShowViewsColumn && !viewsError
      ? {
          views: {
            count: viewsData[getDomain(s)] !== undefined ? viewsData[getDomain(s)] : 0,
            site: s,
            onClick: () => onAnalytics(s),
            timestamp: viewsTimestamps[getDomain(s)],
            tooltip: getHoursRemainingTooltip(viewsTimestamps[getDomain(s)])
          }
        }
      : {}),
    actions:
      isMobile || isTablet
        ? [
            {
              type: 'action_menu',
              props: {
                icon: '/assets/icons/menu_horizontal.svg',
                tooltip: 'More',
                onEditHTML: () => handleEditHTML(s),
                onEditPDF: () => handleEditPDF(s),
                onArchive: () => onArchive(s),
                onDownload: () => onDownload(s),
                onDownloadAll: () => onDownloadAll(s),
                onAnalytics: () => onAnalytics(s),
                onQRCode: () => onQRCode(s),
                onDelete: () => onDelete(s),
                onCopy: () => copyToClipboard(`https://${getDomain(s)}`),
                onCopyDirectLink: () =>
                  copyToClipboard(`https://${getDomain(s)}/${parseFilename(s.filename, getDomain(s))}`),
                onReplaceFile: () => onUpdate(s),
                onEmbed: () => onEmbed(s),
                onAddCustomDomain: () => handlePublishCustomDomain(s),
                onIntegrations: () => setShowIntegrationsModal(true),
                link: getDomain(s),
                type: s.type,
                siteType: s.siteType,
                passwordProtected: s?.passwordProtected,
                enableEmailGate: s?.enableEmailGate,
                inPreviewMode: s?.inPreviewMode,
                onShare: () => onShare(s),
                trial: !!!userProfile?.productId,
                disabled: (s?.linkStatus === 'processing' && !s?.isPresignedUpload) || s?.linkStatus === 'error',
                onUpgrade: () => onUpgrade(PLANS.TINY.id),
                onAssignToSpace: () => onAssignToSpace(s),
                hasSpaces: hasSpaces,
                onExportEmails: () => handleExportEmails(s),
                onViewVersions: () => onViewVersions?.(s),
                isPresignedUpload: s?.isPresignedUpload,
                linkId: s?.id,
                wasmerDnsRecord: s?.wasmerDnsRecord,
                onAnalyseSite: () => onAnalyseSite(s),
                onClearCache: isCustomDomain(getDomain(s)) ? () => onClearCache(s) : undefined,
                table: 'livesites'
              }
            }
          ]
        : [
            {
              icon: '/assets/icons/edit.svg',
              label: 'Update',
              className: 'update-action-button',
              onClick: () => onUpdate(s),
              dataTestId: 'Dropdown-update-button',
              disabled: canNotUpdate(s)
            },
            { icon: '/assets/icons/chart-bar.svg', tooltip: 'Analytics', onClick: () => onAnalytics(s) },
            {
              icon: '/assets/icons/qr-code.svg',
              tooltip: 'QR Code',
              onClick: () => onQRCode(s)
            },
            {
              type: 'action_menu',
              props: {
                icon: '/assets/icons/menu.svg',
                tooltip: 'More',
                onEditHTML: () => handleEditHTML(s),
                onEditPDF: () => handleEditPDF(s),
                onArchive: () => onArchive(s),
                onDownload: () => onDownload(s),
                onDownloadAll: () => onDownloadAll(s),
                onAnalytics: () => onAnalytics(s),
                onQRCode: () => onQRCode(s),
                onDelete: () => onDelete(s),
                onCopy: () => copyToClipboard(`https://${getDomain(s)}`),
                onCopyDirectLink: () =>
                  copyToClipboard(`https://${getDomain(s)}/${parseFilename(s.filename, getDomain(s))}`),
                onReplaceFile: () => onUpdate(s),
                onEmbed: () => onEmbed(s),
                onAddCustomDomain: () => handlePublishCustomDomain(s),
                onIntegrations: () => setShowIntegrationsModal(true),
                link: getDomain(s),
                type: s?.type,
                siteType: s?.siteType,
                passwordProtected: s?.passwordProtected,
                enableEmailGate: s?.enableEmailGate,
                inPreviewMode: s?.inPreviewMode,
                onShare: () => onShare(s),
                trial: !!!userProfile?.productId,
                disabled: s?.linkStatus === 'processing' && !s?.isPresignedUpload,
                onUpgrade: () => onUpgrade(PLANS.TINY.id),
                onAssignToSpace: () => onAssignToSpace(s),
                hasSpaces: hasSpaces,
                onExportEmails: () => handleExportEmails(s),
                onViewVersions: () => onViewVersions?.(s),
                isPresignedUpload: s?.isPresignedUpload,
                linkId: s?.id,
                wasmerDnsRecord: s?.wasmerDnsRecord,
                onAnalyseSite: () => onAnalyseSite(s),
                onClearCache: isCustomDomain(getDomain(s)) ? () => onClearCache(s) : undefined
              }
            }
          ]
  }))

  const canUseAI = true

  const headerButtons = [
    ...(canUseAI
      ? [
          {
            type: 'custom_button',
            component: (
              <BorderButton className="tr-create-with-ai" onClick={() => setShowCreateWithAIModal(true)}>
                Create with
                <Badge className="ms-1">AI</Badge>
              </BorderButton>
            )
          }
        ]
      : []),
    {
      type: 'custom_button',
      component: (
        <UploadDropdown
          onSiteAction={onSiteAction}
          onShowCodeEditor={onShowCodeEditor}
          onOpenTextEditor={onOpenTextEditor}
          disabled={userProfile.status === ACCOUNT_STATUS.PAUSED}
          connectGithub={connectGithub}
          viewRepos={viewRepos}
          onOpenUrl={onOpenUrl}
          onOpenGithubModal={onOpenGithubModal}
          onOpenDropBox={onOpenDropBox}
          onOpenTransferRequestLinkModal={onOpenTransferRequestLinkModal}
          isMobile={isMobile}
        />
      )
    }
    // Add more buttons
  ]

  useEffect(() => {
    ;(async () => {
      if (isCancelPresignedUpload === 'true' && allSites?.length > 0) {
        localStorage.removeItem('cancelPresignedUpload')

        for (const site of allSites) {
          if (site.isPresignedUpload && site.linkStatus === 'processing') {
            const formData = new FormData()
            await cancelPresignedUpload({
              domain: getDomain(site),
              formData,
              config: {}
            })
          }
        }
      }
    })()
  }, [allSites, isCancelPresignedUpload])

  const handleCancelUploads = useCallback(async () => {
    const hasPresignedUploads = loadingSites?.some((site) => site.isPresignedUpload && site.linkStatus === 'processing')

    if (hasPresignedUploads) {
      for (const site of loadingSites) {
        if (site.isPresignedUpload && site.linkStatus === 'processing') {
          const formData = new FormData()
          await cancelPresignedUpload({
            domain: getDomain(site),
            formData,
            config: {}
          })
        }
      }
    }
  }, [loadingSites])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const hasPresignedUploads = loadingSites?.some(
        (site) => site.isPresignedUpload && site.linkStatus === 'processing'
      )

      if (hasPresignedUploads) {
        handleCancelUploads()
        event.preventDefault()
        /** Chrome requires returnValue to be set */
        event.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleCancelUploads, loadingSites])

  return (
    <>
      <CustomTable
        headers={headers}
        data={data}
        title={activeSpace && activeSpace !== 'all' && currentSpaceName ? currentSpaceName : 'Live Projects'}
        badge={isMobile ? null : badge}
        headerButtons={headerButtons}
        emptyStateMessage="No live projects"
        emptyStateButton={isMobile ? undefined : { text: 'Upload file', onClick: () => onSiteAction('CREATE') }}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        showSearch={sites?.length > 4}
        disabled={userProfile.status === ACCOUNT_STATUS.PAUSED}
        onShare={onShare}
        spaceId={activeSpace}
        spaceName={currentSpaceName}
        onEditSpace={onEditSpace}
        onDeleteSpace={onDeleteSpace}
        isSpace={activeSpace && activeSpace !== 'all' && activeSpace !== 'archive'}
        hideSpacesProjects={hideSpacesProjects}
        onToggleHideSpacesProjects={onToggleHideSpacesProjects}
        hasSpaces={hasSpaces}
      />

      <CreateWithAIModal show={showCreateWithAIModal} onHide={() => setShowCreateWithAIModal(false)} />

      {/* Custom Domain Selector Modal */}
      {customDomainModalData && (
        <CustomDomainSelectorModal
          show={true}
          onHide={() => setCustomDomainModalData(null)}
          validatedDomains={customDomainModalData?.validatedDomains}
          site={customDomainModalData?.site}
          onAddNewDomain={() => {
            const site = customDomainModalData?.site
            const siteDomain = site?.subdomain || site?.link
            const isServerFile = siteDomain?.includes('tiiny.io') || isWasmerFile(site)
            if (isServerFile) {
              localStorage.setItem('isServerFileForCustomDomain', 'true')
            } else {
              localStorage.removeItem('isServerFileForCustomDomain')
            }
            setCustomDomainModalData(null)
            onAddCustomDomain(site)
          }}
          onUpdate={(site) => {
            setCustomDomainModalData(null)
            onSiteAction(ACTION_UPDATE, site)
          }}
        />
      )}

      {/* Integrations Modal */}
      <IntegrationsModal show={showIntegrationsModal} onClose={() => setShowIntegrationsModal(false)} />
    </>
  )
}

export default LiveSitesTable
